/**
 * `Five9CrmAdapter` — React shell for the Five9 Agent Desktop Toolkit
 * (ADT) iframe. The iframe DOM node itself is owned outside React (see
 * `persistentFive9Iframe.js`) so the Five9 session survives any React
 * unmount / route change / auth handover.
 *
 * Responsibilities:
 *   - Render the header + resize / expand controls; move the singleton
 *     iframe into/out of the body wrapper via `mountInto` / `detach`.
 *   - Poll for `window.Five9.CrmSdk` and call `crmApi.registerApi(...)`
 *     with callbacks built by `useFive9CrmCallbacks`. Emits `sdk.*`
 *     lifecycle events (`sdk.registered`, `sdk.timeout`, `sdk.registerFailed`).
 *   - After registration succeeds, best-effort probe any available
 *     `getActiveInteraction` / `getInteractions` / `getCurrentInteraction`
 *     for post-reload rehydration (`sdk.rehydrate`) and subscribe to the
 *     interaction lifecycle stream used by the backend call publisher.
 *   - Install document-level, capture-phase drag-cancel listeners scoped
 *     to the iframe by `data-five9-adt="singleton"` so react-dnd never
 *     eats mousedown/click inside Five9's UI (regression that surfaced
 *     when Chrome DevTools was open).
 *   - Emit `adapter.mounted` / `adapter.unmounted` diagnostics.
 *   - Wire cross-tab call-state sync (`useFive9CallStateSync`) so
 *     lock-handover between tabs doesn't lose the current association.
 *
 * The component is mounted ABOVE `<Auth>` in `App.js` and is permanently
 * mounted — visibility is controlled entirely by the `hidden` prop.
 *
 * See `docs/five9-integration.md` for the full data-flow diagram and
 * developer guide (§ 12 — "Developer path — how to interact with Five9").
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  IconButton,
  Paper,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';
import useFive9CrmCallbacks from 'src/hooks/five9/useFive9CrmCallbacks';
import useFive9SingleTabLock from 'src/hooks/five9/useFive9SingleTabLock';
import useFive9CallStateSync from 'src/hooks/five9/useFive9CallStateSync';
import useFive9CallPublisher, { isFive9CallPublisherEnabled } from 'src/hooks/five9/useFive9CallPublisher';
import {
  upsertCall,
  setActiveCall,
  clearCall,
  resetFive9,
  hydrateFive9
} from 'src/actions/five9Actions';
import logger from 'src/utils/five9/five9Logger';
import { mountInto as mountFive9IframeInto, detach as detachFive9Iframe, reset as resetFive9Iframe } from 'src/utils/five9/persistentFive9Iframe';
import subscribeToInteractionApi from 'src/utils/five9/subscribeToInteractionApi';
import loadFive9Sdk from 'src/utils/five9/loadFive9Sdk';
import extractCall from 'src/utils/five9/extractCall';
import getCallDisplayNumber, { isCallInWrapUp } from 'src/utils/five9/getCallDisplayNumber';
import formatters from 'src/utils/formatters';
import { clearPersistedFive9, readPersistedFive9 } from 'src/store';
import { clearPersistedFive9CallQueues } from 'src/utils/five9/five9CallQueueStorage';
import { resetClick2DialRegistration } from 'src/utils/five9/click2DialRegistration';

const SDK_POLL_INTERVAL_MS = 5000;
const SDK_LOAD_TIMEOUT_MS = 30000;
// Five9 raises no event on session timeout/logout, so a call stuck after `callEnded`
// (no `callFinished`) is cleared once it has been idle this long.
const ENDED_CALL_IDLE_TTL_MS = 15 * 60 * 1000;
const ENDED_CALL_SWEEP_INTERVAL_MS = 60 * 1000;
const FIVE9_AUTH_TRANSITION_BLOCK_KEY = 'five9:authTransitionBlocked';
// Stable empty-object reference for the cross-identity render-time gate
// below, so passing "no calls" during a user switch doesn't itself cause
// an extra reconcile due to a new object identity every render.
const EMPTY_CALLS = Object.freeze({});

// Default size chosen to comfortably fit the Five9 ADT login form + adapter UI.
// The ADT wraps its softphone-status row and scrolls below ~320px wide — its
// side margins are internal ADT padding. Maximize toggle covers heavy use.
const DEFAULT_PANEL_WIDTH = 320;
const DEFAULT_PANEL_HEIGHT = 585;
const PANEL_POSITION_STORAGE_KEY = 'five9:panelPosition';

// Keep at least part of the header on screen so the panel can always be dragged back.
const clampPanelPosition = ({ left, top }, width = DEFAULT_PANEL_WIDTH) => ({
  left: Math.min(Math.max(left, 0), Math.max(window.innerWidth - Math.min(width, 80), 0)),
  top: Math.min(Math.max(top, 0), Math.max(window.innerHeight - 32, 0))
});

// Clamped because the position may have been saved on a larger viewport.
const readStoredPanelPosition = () => {
  try {
    const parsed = JSON.parse(window.localStorage?.getItem(PANEL_POSITION_STORAGE_KEY) || 'null');
    return Number.isFinite(parsed?.left) && Number.isFinite(parsed?.top)
      ? clampPanelPosition(parsed)
      : null;
  } catch (_) {
    return null;
  }
};

// Header summary of the active call's screenpop_core_* CAVs: blank values
// dropped, company then DID first, remaining fields in payload order.
const SCREENPOP_SUMMARY_LABELS = { company_name: 'Company', did: 'DID' };
const SCREENPOP_SUMMARY_RANK = { company_name: 0, did: 1 };
const buildScreenpopSummary = (screenpopCore) => {
  if (!screenpopCore || typeof screenpopCore !== 'object') return null;
  const entries = Object.entries(screenpopCore)
    .map(([key, entry]) => [key.replace(/^screenpop_core_/, ''), String(entry?.value ?? '').trim()])
    .filter(([, value]) => value !== '')
    .sort(([a], [b]) => (SCREENPOP_SUMMARY_RANK[a] ?? 2) - (SCREENPOP_SUMMARY_RANK[b] ?? 2));
  if (!entries.length) return null;
  return entries
    .map(([key, value]) => {
      const label = SCREENPOP_SUMMARY_LABELS[key]
        ?? key.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      return `${label}: ${value}`;
    })
    .join(' · ');
};

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    width: DEFAULT_PANEL_WIDTH,
    // zIndex.tooltip is 1500; go above it to make sure nothing in the app
    // ever floats over the iframe and steals pointer events.
    zIndex: theme.zIndex.tooltip + 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: theme.shadows[8],
    // Ensure this stacking context receives pointer events even if an
    // ancestor sets pointer-events: none.
    pointerEvents: 'auto'
  },
  rootMaximized: {
    top: theme.spacing(2),
    left: theme.spacing(2),
    right: theme.spacing(2),
    bottom: theme.spacing(2),
    width: 'auto'
  },
  hidden: {
    display: 'none'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    // Header doubles as the drag handle for repositioning the panel.
    cursor: 'move',
    userSelect: 'none',
    touchAction: 'none',
    flex: '0 0 auto'
  },
  title: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.pxToRem(14),
    // The brand label never truncates; the badges absorb all shrinkage.
    flexShrink: 0
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    // Shrinkable below content width so the badge ellipses can engage;
    // without minWidth 0 the badges' min-content width pushes the action
    // buttons out of the fixed-width panel.
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden'
  },
  callBadge: {
    fontSize: theme.typography.pxToRem(12),
    opacity: 0.9,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 170,
    minWidth: 0,
    flex: '0 1 auto'
  },
  screenpopBadgeWrap: {
    display: 'inline-flex',
    minWidth: 0
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    // The window controls must never be shrunk or pushed out of view.
    flex: '0 0 auto',
    marginLeft: theme.spacing(0.5)
  },
  toggleButton: {
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(0.25)
  },
  body: {
    position: 'relative',
    height: DEFAULT_PANEL_HEIGHT,
    width: '100%',
    backgroundColor: theme.palette.background.default,
    flex: '1 1 auto',
    // Establish a new stacking context so the iframe always sits above any
    // sibling positioned content within the panel.
    isolation: 'isolate'
  },
  bodyMaximized: {
    height: 'auto'
  },
  bodyCollapsed: {
    display: 'none'
  },
  iframe: {
    display: 'block',
    position: 'absolute',
    inset: 0,
    height: '100%',
    width: '100%',
    border: 'none',
    // Guarantee the iframe receives clicks/keys even under aggressive resets.
    pointerEvents: 'auto',
    zIndex: 1
  },
  blockedOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    textAlign: 'center',
    backgroundColor: theme.palette.background.paper,
    zIndex: 2
  },
  blockedOverlayContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1)
  }
}));

/**
 * NOTE: The Five9 iframe is intentionally NOT rendered as a React child of
 * this component. It is owned by `persistentFive9Iframe.js` as a singleton
 * DOM element parked under `document.body`. On mount we `appendChild` it
 * into our body wrapper (`iframeContainerRef`); on unmount we return it to
 * `document.body` (hidden). Because `appendChild` MOVES the existing DOM
 * node instead of recreating it, the Five9 session — including any active
 * call — survives even if this component is unmounted (e.g. by an
 * auth-guard flicker, Suspense boundary, or layout swap on route change).
 */

/**
 * Persistent Five9 CRM ADT adapter.
 *
 * - Waits for the Five9 CRM SDK (loaded dynamically by `loadFive9Sdk.js`)
 *   to become available on window and registers the CRM API callbacks exactly
 *   once per authenticated session.
 * - Renders the ADT iframe in a fixed floating panel and never unmounts it,
 *   so the Five9 session survives all route changes.
 * - Uses `useFive9SingleTabLock` to prevent (or warn about) concurrent tabs.
 *
 * Must be mounted inside <Auth> so that `state.account.user` is set before
 * registration runs. Callbacks are stubs today; see useFive9CrmCallbacks.
 *
 * @param {Object} props
 * @param {boolean} [props.hidden] - When true, the panel is visually hidden but stays mounted (preserves the Five9 session).
 * @returns {JSX.Element|null}
 */
function Five9CrmAdapter({ hidden }) {
  const classes = useStyles();
  // Read once per render rather than inline at the bottom render-gate below,
  // so the SAME "no SDK configured" decision also gates publisher init —
  // otherwise the render-only null-return left the queue/reconcile/drain
  // path running for an authenticated user even though nothing rendered.
  const isSdkConfigured = Boolean(process.env.REACT_APP_FIVE9_SDK_URL);
  const { user } = useSelector((state) => state.account);
  const activeCallSid = useSelector((s) => s?.five9?.activeCallSid || null);
  const calls = useSelector((s) => s?.five9?.calls || {});
  const activeCallNumber = useSelector((s) => {
    const id = s?.five9?.activeCallSid;
    const call = id ? s.five9.calls?.[id] : null;
    if (!call) return null;
    // Shared with the TopBar call chip so both surfaces name the same party.
    return getCallDisplayNumber(call);
  });
  const activeScreenpopSummary = useSelector((s) => {
    const id = s?.five9?.activeCallSid;
    return buildScreenpopSummary(id ? s.five9.calls?.[id]?.screenpopCore : null);
  });
  const isActiveCallEnded = useSelector((s) => {
    const id = s?.five9?.activeCallSid;
    return isCallInWrapUp(id ? s.five9.calls?.[id] : null);
  });
  const reduxDispatch = useDispatch();
  const callbacks = useFive9CrmCallbacks();
  const { isBlocked, isReady, requestTakeover } = useFive9SingleTabLock({
    enabled: Boolean(user)
  });
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [panelPosition, setPanelPosition] = useState(readStoredPanelPosition);
  const panelRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setPanelPosition((current) => (
        current ? clampPanelPosition(current, panelRef.current?.getBoundingClientRect().width) : current
      ));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleHeaderPointerDown = (event) => {
    if (isMaximized || event.button !== 0 || event.target.closest('button')) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = { offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top, width: rect.width };
    // Capture keeps move events coming even when the pointer passes over the Five9 iframe.
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleHeaderPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    drag.last = clampPanelPosition({
      left: event.clientX - drag.offsetX,
      top: event.clientY - drag.offsetY
    }, drag.width);
    setPanelPosition(drag.last);
  };

  const handleHeaderPointerUp = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (!drag.last) return;
    try {
      window.localStorage?.setItem(PANEL_POSITION_STORAGE_KEY, JSON.stringify(drag.last));
    } catch (_) { /* noop */ }
  };

  const handleHeaderDoubleClick = (event) => {
    if (event.target.closest('button')) return;
    setPanelPosition(null);
    try { window.localStorage?.removeItem(PANEL_POSITION_STORAGE_KEY); } catch (_) { /* noop */ }
  };
  const [isAuthTransitionBlocked, setIsAuthTransitionBlocked] = useState(() => {
    try {
      if (typeof window === 'undefined') return false;
      const isSessionBlocked = window.sessionStorage?.getItem(FIVE9_AUTH_TRANSITION_BLOCK_KEY) === 'true';
      // Legacy cleanup: prior builds stored this in localStorage, which
      // outlives browser sessions and can strand users in a blocked state.
      try { window.localStorage?.removeItem(FIVE9_AUTH_TRANSITION_BLOCK_KEY); } catch (_) { /* noop */ }
      return isSessionBlocked;
    } catch (_) {
      return false;
    }
  });
  const isAuthTransitionBlockedRef = useRef(isAuthTransitionBlocked);
  const syncScope = user?.id ? `user:${user.id}` : null;
  const isCallPublisherEnabled = isFive9CallPublisherEnabled();
  // The auth-transition purge (resetFive9()/clearPersistedFive9CallQueues())
  // only runs inside a useEffect, which commits AFTER this render. On the
  // very render where `user.id` first changes to a new signed-in identity,
  // `calls` (from Redux) is still the PREVIOUS user's call state, and
  // `isAuthTransitionBlocked` hasn't been set yet — a same-render window
  // where useFive9CallPublisher would otherwise create/enable a queue for
  // the NEW identity and immediately reconcile/drain using the OLD user's
  // calls. Detect this synchronously during render (not in an effect) via a
  // plain ref comparison, so the gate applies to this render, not the next.
  const previousRenderUserIdRef = useRef(undefined);
  const isUserSwitchingIdentity = previousRenderUserIdRef.current != null
    && user?.id != null
    && previousRenderUserIdRef.current !== user.id;
  previousRenderUserIdRef.current = user?.id ?? null;
  useFive9CallStateSync({
    enabled: Boolean(syncScope) && !isAuthTransitionBlocked,
    scope: syncScope
  });
  useFive9CallPublisher({
    // Three independent gates compose here: the SDK-URL gate ("Five9 isn't
    // configured at all"), `isCallPublisherEnabled` (ops rollout opt-out
    // AND the backend base-URL fail-closed check — see
    // `isFive9CallPublisherEnabled()`), and `isUserSwitchingIdentity` (the
    // same-render cross-identity window above, distinct from
    // `isAuthTransitionBlocked` which only covers renders AFTER the
    // transition effect has committed). Any one of them alone fully
    // disables queue creation, reconciliation, and drain.
    enabled: isSdkConfigured && isCallPublisherEnabled && Boolean(user)
      && isReady && !isBlocked && !isAuthTransitionBlocked && !isUserSwitchingIdentity,
    identity: (isSdkConfigured && !isUserSwitchingIdentity) ? (user?.id || null) : null,
    calls: isUserSwitchingIdentity ? EMPTY_CALLS : calls
  });

  // Auto-collapse the panel when a peer tab owns the lock so the
  // "already open in another tab" state doesn't take up screen real estate
  // in a tab that can't use Five9 anyway. On regaining ownership we
  // auto-expand back. The user can still manually toggle either way
  // afterwards — this effect only fires on the `isBlocked` transition.
  useEffect(() => {
    setIsCollapsed(isBlocked);
  }, [isBlocked]);
  const didRegisterRef = useRef(false);
  const bodyRef = useRef(null);
  const iframeContainerRef = useRef(null);
  const lastHydratedUserIdRef = useRef(null);
  // Tracks the user identity used for the CURRENT Five9 session so we can
  // detect logout / user-switch and tear down before a new session starts.
  // Undefined sentinel (not null) so the initial mount with `user == null`
  // is not misread as a transition FROM a signed-in user.
  const previousUserIdRef = useRef(undefined);

  // Auth-transition purge. The adapter is mounted ABOVE <Auth>, so the
  // iframe/session/redux slice would otherwise carry a previous user's
  // Five9 context into a subsequent signed-in session in the same tab
  // (logout → login, or account switch). On every identity transition:
  //   - drop the Five9 redux slice (`resetFive9()`),
  //   - remove the sessionStorage snapshot (`clearPersistedFive9()`),
  //   - drop the module-level pending click2dial registration
  //     (`resetClick2DialRegistration()`) — it lives outside redux, so the
  //     slice purge alone would leave a stale dispatchId armed,
  //   - reset `didRegisterRef` so the next SDK register actually runs,
  //   - destroy the singleton iframe (`resetFive9Iframe()`) so the next
  //     `mountInto` starts from a fresh DOM node.
  // Five9 does not expose a supported browser-session logout/reset in this
  // codebase, so we also fail closed for the current browser profile after
  // a user switch: once this flag is set, the adapter will not auto-mount or
  // auto-register again until the flag is explicitly cleared.
  //
  // Reset ONLY fires when transitioning FROM a signed-in user (previousId
  // is a real id). The two "not a real transition" cases are skipped:
  //   - `undefined → null`  initial mount before auth resolves,
  //   - `null → id`         fresh sign-in of a fresh tab (e.g. tab was
  //                         inadvertently closed and reopened).
  useEffect(() => {
    const currentId = user?.id ?? null;
    const previousId = previousUserIdRef.current;
    const isFromSignedInUser = previousId != null;
    const isLogoutTransition = isFromSignedInUser && currentId == null;
    const isDirectUserSwitch = isFromSignedInUser && currentId != null && previousId !== currentId;
    const isRealTransition = isLogoutTransition || isDirectUserSwitch;
    if (isRealTransition) {
      logger.event('adapter.authTransition', { from: previousId, to: currentId });
      reduxDispatch(resetFive9());
      clearPersistedFive9();
      clearPersistedFive9CallQueues();
      resetClick2DialRegistration();
      didRegisterRef.current = false;
      resetFive9Iframe();
      try {
        if (isDirectUserSwitch) {
          isAuthTransitionBlockedRef.current = true;
          setIsAuthTransitionBlocked(true);
          if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.setItem(FIVE9_AUTH_TRANSITION_BLOCK_KEY, 'true');
          }
        } else {
          // Logout is treated as a full re-auth boundary for this app.
          isAuthTransitionBlockedRef.current = false;
          setIsAuthTransitionBlocked(false);
          if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.removeItem(FIVE9_AUTH_TRANSITION_BLOCK_KEY);
          }
        }
      } catch (_) { /* noop */ }
    }
    previousUserIdRef.current = currentId;
  }, [user, reduxDispatch]);

  // Late rehydration: once the current user is known, pull the identity-scoped
  // persisted Five9 snapshot from sessionStorage and hydrate the store before
  // the rest of the Five9 runtime starts relying on call context. This keeps
  // store bootstrap identity-agnostic and prevents stale snapshots from being
  // applied before auth has resolved.
  useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (currentUserId == null) {
      lastHydratedUserIdRef.current = null;
      return undefined;
    }
    if (lastHydratedUserIdRef.current === currentUserId) return undefined;
    const persisted = readPersistedFive9(currentUserId);
    lastHydratedUserIdRef.current = currentUserId;
    if (!persisted) return undefined;
    reduxDispatch(hydrateFive9(persisted));
    return undefined;
  }, [reduxDispatch, user?.id]);

  const callsRef = useRef(calls);
  callsRef.current = calls;
  useEffect(() => {
    const sweep = () => {
      const now = Date.now();
      Object.values(callsRef.current).forEach((call) => {
        if (!call?.endedAt) return;
        const idleMs = now - (call.lastEventAt || call.endedAt);
        if (idleMs < ENDED_CALL_IDLE_TTL_MS) return;
        logger.event('call.staleCleared', { callSid: call.callSid, endedAt: call.endedAt, idleMs });
        reduxDispatch(clearCall(call.callSid));
      });
    };
    sweep();
    const id = setInterval(sweep, ENDED_CALL_SWEEP_INTERVAL_MS);
    return () => clearInterval(id);
  }, [reduxDispatch]);

  // Diagnostic: log every mount/unmount of the adapter so we can see in the
  // Five9 log panel whether route changes are tearing the component down.
  // If they are, the persistent-iframe singleton (see `persistentFive9Iframe`)
  // still keeps the Five9 session alive across the remount.
  useEffect(() => {
    logger.event('adapter.mounted', {});
    return () => {
      logger.event('adapter.unmounted', {});
    };
  }, []);

  // Adopt the singleton Five9 iframe into our body wrapper on mount, and
  // release it back to `document.body` on unmount so it survives.
  //
  // Gated on `isBlocked`: a tab that has LOST the single-tab-lock election
  // must not host the ADT iframe at all — otherwise the SDK inside it
  // continues to run alongside the owning tab's SDK, defeating the
  // single-tab guarantee. When blocked, the iframe is detached (moved
  // back under document.body, hidden). When ownership is regained, it's
  // re-mounted — the DOM node itself is preserved (see
  // persistentFive9Iframe.js) so the Five9 session survives the handover.
  // Gated on `isReady && !isBlocked`: a newcomer tab must NOT mount the ADT
  // iframe during the ~750ms lock probe window, otherwise the SDK inside
  // it starts loading before we learn another tab already owns the lock —
  // exactly the multi-tab race the single-tab guarantee is meant to
  // prevent. Also gated on `user`: logout must leave the singleton fully
  // detached so the auth-transition purge cannot be undone by lazily
  // recreating the iframe while the app is signed out. Once the election
  // resolves: owner mounts, follower stays detached, and ownership
  // handovers / subsequent logins re-run this effect.
  useEffect(() => {
    if (!isReady) return undefined;
    if (!user) {
      detachFive9Iframe();
      return undefined;
    }
    if (isAuthTransitionBlockedRef.current) {
      detachFive9Iframe();
      return undefined;
    }
    if (isBlocked) {
      detachFive9Iframe();
      return undefined;
    }
    if (iframeContainerRef.current) {
      mountFive9IframeInto(iframeContainerRef.current);
    }
    return () => {
      detachFive9Iframe();
    };
    // `user?.id` so the freshly-created iframe from the auth-transition
    // reset above (which destroys the singleton) is adopted into our
    // container on the next login. Without this dep, the singleton would
    // stay parked under document.body until an unrelated `isBlocked` flip.
  }, [isReady, isBlocked, user?.id]);

  // react-dnd's HTML5Backend attaches WINDOW-level `dragstart` listeners in
  // the capture phase. When a user mouses down on a cross-origin iframe, the
  // browser may fire `dragstart` on the iframe element BEFORE the click is
  // handed off to the iframe's document, which manifests as "mouse clicks
  // don't work but Tab / Space still do". This is trivially reproducible
  // when Chrome DevTools is open because the extra debugging overhead makes
  // the drag-vs-click race deterministic.
  //
  // We install capture-phase listeners on `document` (which run before
  // window-level bubble listeners and at the same phase level as window
  // capture listeners) and only cancel drag events whose target is our
  // Five9 iframe (identified by the `data-five9-adt` attribute set in
  // persistentFive9Iframe.js). This surgically prevents the drag start on
  // the iframe without interfering with any other drag/drop in the app.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const isFive9Target = (event) => {
      const t = event.target;
      return t && t.nodeType === Node.ELEMENT_NODE && (
        t.tagName === 'IFRAME' && t.getAttribute && t.getAttribute('data-five9-adt') === 'singleton'
      );
    };
    const cancel = (event) => {
      if (!isFive9Target(event)) return;
      event.preventDefault();
      event.stopPropagation();
    };
    const events = ['dragstart', 'drag', 'dragend', 'dragover', 'drop'];
    events.forEach((name) => document.addEventListener(name, cancel, true));
    return () => {
      events.forEach((name) => document.removeEventListener(name, cancel, true));
    };
  }, []);

  const appInsights = useAppInsightsContext();
  const trackSdkTimeout = useTrackEvent(
    appInsights,
    'DispatchQoreFE::Five9::SdkLoadTimeout',
    null
  );
  const trackSdkRegistered = useTrackEvent(
    appInsights,
    'DispatchQoreFE::Five9::SdkRegistered',
    null
  );

  useEffect(() => {
    if (!user) return undefined;
    // Wait for the single-tab lock election to resolve before touching the
    // SDK. Without this gate, a newcomer tab would call `loadFive9Sdk`
    // and begin the register-poll loop during the ~750ms probe window,
    // then discover it lost the election — by which time the iframe SDK
    // is already running alongside the owning tab's SDK.
    if (!isReady) return undefined;
    // Non-owner tabs must not register callbacks or subscribe to the
    // interaction API — otherwise the SDK inside the (also-detached)
    // iframe would still be relaying events, allowing duplicate call
    // handling across tabs. When ownership flips back, this effect
    // re-runs and we either subscribe fresh (if already registered) or
    // start the full poll/register flow.
    if (isAuthTransitionBlockedRef.current) return undefined;
    if (isBlocked) return undefined;
    if (!process.env.REACT_APP_FIVE9_SDK_URL) {
      // No SDK configured for this environment; nothing to register.
      logger.info(
        'sdk.notConfigured',
        {},
        'REACT_APP_FIVE9_SDK_URL not set; skipping SDK registration.'
      );
      return undefined;
    }

    const started = Date.now();
    let pollId = null;
    let timeoutId = null;
    // The SDK loader promise can resolve AFTER this effect's cleanup (tab
    // lost ownership / unmounted); registering then would leave a live
    // interactionApi subscription in a blocked tab with no cleanup to come.
    let disposed = false;

    /**
     * After a successful register, ask the SDK whether it already has an
     * active interaction (survives hard reloads because the Five9 iframe
     * session is cookie-backed inside app.five9.com). If the SDK exposes a
     * probe method we seed the redux slice so dispatch views don't have to
     * wait for the next callback to fire; if it doesn't, we log one
     * `sdk.rehydrate.unsupported` event and rely on sessionStorage
     * rehydration (see store/index.js).
     *
     * @returns {void}
     */
    const reprobeActiveInteraction = () => {
      try {
        const sdk = window.Five9?.CrmSdk;
        const crmApi = sdk?.crmApi?.();
        const probe = crmApi?.getActiveInteraction
          || crmApi?.getInteractions
          || crmApi?.getCurrentInteraction;
        if (typeof probe !== 'function') {
          logger.info('sdk.rehydrate.unsupported', {});
          return;
        }
        const result = probe.call(crmApi);
        Promise.resolve(result).then((data) => {
          let list;
          if (Array.isArray(data)) list = data;
          else if (data) list = [data];
          else list = [];
          if (!list.length) {
            logger.info('sdk.rehydrate', { count: 0 });
            return;
          }
          // Route the probe results through `extractCall` so non-call
          // interactions (email/chat/name-object refs) can't pollute the
          // `five9.calls` map. Prior versions accepted any truthy
          // `interactionId`/`id`, which bypassed the guards `extractCall`
          // and the reducer both rely on elsewhere.
          const validCalls = list.map(extractCall).filter(Boolean);
          if (!validCalls.length) {
            logger.info('sdk.rehydrate', { count: 0, rawCount: list.length });
            return;
          }
          validCalls.forEach((call) => {
            reduxDispatch(upsertCall(call));
          });
          reduxDispatch(setActiveCall(validCalls[0].callSid));
          logger.event('sdk.rehydrate', { count: validCalls.length });
        }).catch((err) => {
          logger.warn('sdk.rehydrate.error', { message: err?.message });
        });
      } catch (err) {
        logger.warn('sdk.rehydrate.error', { message: err?.message });
      }
    };

    let unsubscribeInteractionApi = null;

    const tryRegister = () => {
      const sdk = window.Five9?.CrmSdk;
      if (!sdk || typeof sdk.crmApi !== 'function') return false;
      try {
        const crmApi = sdk.crmApi();
        if (!didRegisterRef.current) {
          crmApi.registerApi(callbacks);
          didRegisterRef.current = true;
          const elapsedMs = Date.now() - started;
          trackSdkRegistered({ elapsedMs });
          logger.event('sdk.registered', { elapsedMs });
          reprobeActiveInteraction();
        } else {
          // We had registered previously and are regaining ownership after
          // being blocked by a peer. The SDK inside the (preserved) iframe
          // still holds our callbacks; only the interactionApi subscription
          // needs re-establishing.
          logger.info('sdk.rebound', { note: 'ownership regained; re-subscribing to interactionApi' });
        }
        // Defensive: never stack a second subscription on top of a live one
        // for this run. Overlapping listener objects make the SDK invoke
        // both handlers for the same event, which is what produced
        // duplicated `interaction.*` telemetry.
        if (typeof unsubscribeInteractionApi === 'function') {
          try { unsubscribeInteractionApi(); } catch (_) { /* noop */ }
          unsubscribeInteractionApi = null;
        }
        unsubscribeInteractionApi = subscribeToInteractionApi(reduxDispatch, {
          publishCallLifecycle: isCallPublisherEnabled
        });
        return true;
      } catch (err) {
        // Return false so the poll loop keeps trying (up to `sdk.timeout`)
        // instead of latching a transient register failure as permanent.
        logger.error('sdk.registerFailed', { message: err?.message });
        return false;
      }
    };

    const registerIfNeeded = () => {
      if (disposed) return true;
      // `didRegisterRef` survives effect re-runs (it tracks whether the SDK
      // inside the preserved iframe already has our crmApi callbacks), but
      // `unsubscribeInteractionApi` is per effect run and the cleanup tears
      // the subscription down. Gating solely on `didRegisterRef` therefore
      // made `tryRegister()` unreachable after the first registration,
      // leaving the "sdk.rebound" re-subscribe branch as dead code and the
      // tab with NO interactionApi subscription after regaining ownership.
      // Require BOTH: crmApi registered AND a live subscription for this run.
      if (didRegisterRef.current && unsubscribeInteractionApi) {
        if (pollId) {
          clearInterval(pollId);
          pollId = null;
        }
        return true;
      }
      const registered = tryRegister();
      if (registered && pollId) {
        clearInterval(pollId);
        pollId = null;
      }
      return registered;
    };

    // Kick off dynamic script injection. The loader is idempotent, so
    // re-runs on ownership handover (isBlocked flipping false again) or
    // React StrictMode double-invoke don't add duplicate <script> tags.
    // Re-attempt registration as soon as the loader resolves, then keep
    // the 5s poll loop below only as a fallback for slower SDK attachment.
    loadFive9Sdk(process.env.REACT_APP_FIVE9_SDK_URL)
      .then(() => {
        registerIfNeeded();
      })
      .catch((err) => {
        logger.error('sdk.loadFailed', { message: err?.message });
      });

    if (!registerIfNeeded()) {
      pollId = setInterval(() => {
        registerIfNeeded();
      }, SDK_POLL_INTERVAL_MS);
      timeoutId = setTimeout(() => {
        if (!didRegisterRef.current) {
          if (pollId) clearInterval(pollId);
          trackSdkTimeout({ timeoutMs: SDK_LOAD_TIMEOUT_MS });
          logger.warn(
            'sdk.timeout',
            { timeoutMs: SDK_LOAD_TIMEOUT_MS },
            'Five9 CRM SDK never became available.'
          );
        }
      }, SDK_LOAD_TIMEOUT_MS);
    }

    return () => {
      disposed = true;
      if (pollId) clearInterval(pollId);
      if (timeoutId) clearTimeout(timeoutId);
      if (typeof unsubscribeInteractionApi === 'function') {
        try { unsubscribeInteractionApi(); } catch (_) { /* noop */ }
        unsubscribeInteractionApi = null;
      }
    };
    // callbacks is memoized; re-run when the signed-in identity changes (not on every
    // new `user` object), the lock election resolves (isReady), or ownership flips (isBlocked).
  }, [user?.id, isBlocked, isReady, isCallPublisherEnabled]);

  if (!isSdkConfigured) return null;

  const rootClassName = clsx(
    classes.root,
    isMaximized && classes.rootMaximized,
    hidden && classes.hidden
  );
  const bodyClassName = clsx(
    classes.body,
    isMaximized && classes.bodyMaximized,
    isCollapsed && classes.bodyCollapsed
  );
  const headerCallText = activeCallSid
    ? `call ${activeCallNumber ? formatters.formatTelephoneNumber(activeCallNumber) : activeCallSid}${isActiveCallEnded ? ' — wrap-up' : ''}`
    : null;

  return (
    <Paper
      ref={panelRef}
      className={rootClassName}
      elevation={8}
      style={panelPosition && !isMaximized
        ? {
          left: panelPosition.left, top: panelPosition.top, bottom: 'auto', right: 'auto'
        }
        : undefined}
    >
      <div
        className={classes.header}
        onPointerDown={handleHeaderPointerDown}
        onPointerMove={handleHeaderPointerMove}
        onPointerUp={handleHeaderPointerUp}
        onPointerCancel={handleHeaderPointerUp}
        onDoubleClick={handleHeaderDoubleClick}
        title="Drag to move. Double-click to reset position."
      >
        <div className={classes.headerLeft}>
          <Typography className={classes.title}>Five9</Typography>
          {headerCallText && (
            <Typography component="span" className={classes.callBadge} title={headerCallText}>
              {headerCallText}
            </Typography>
          )}
          {headerCallText && activeScreenpopSummary && (
            // Wrapper carries the full untruncated summary as the hover title.
            <span className={classes.screenpopBadgeWrap} title={activeScreenpopSummary}>
              <Typography component="span" className={classes.callBadge}>
                {activeScreenpopSummary}
              </Typography>
            </span>
          )}
        </div>
        <div className={classes.headerActions}>
          <IconButton
            size="small"
            className={classes.toggleButton}
            onClick={() => setIsMaximized((prev) => !prev)}
            aria-label={isMaximized ? 'Restore Five9 panel' : 'Maximize Five9 panel'}
          >
            {isMaximized ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
          </IconButton>
          <IconButton
            size="small"
            className={classes.toggleButton}
            onClick={() => setIsCollapsed((prev) => !prev)}
            aria-label={isCollapsed ? 'Expand Five9 panel' : 'Collapse Five9 panel'}
          >
            {isCollapsed ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </div>
      </div>
      <div className={bodyClassName} ref={bodyRef}>
        {isAuthTransitionBlocked && (
          <div className={classes.blockedOverlay}>
            <Typography variant="body2">
              Five9 was invalidated after an auth transition.
              {' '}
              A fresh browser session is required before Five9 can be used again.
            </Typography>
          </div>
        )}
        {/* The Five9 iframe DOM node is owned by `persistentFive9Iframe` and
            appended here on mount; it is moved (not recreated) so the session
            survives even if this component unmounts and remounts. */}
        <div ref={iframeContainerRef} className={classes.iframe} />
        {!isAuthTransitionBlocked && isBlocked && (
          <div className={classes.blockedOverlay}>
            <div className={classes.blockedOverlayContent}>
              <Typography variant="body2">
                Five9 is already open in another tab. Close the other tab or click below to activate here.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={requestTakeover}
              >
                Use Five9 In This Tab
              </Button>
            </div>
          </div>
        )}
      </div>
    </Paper>
  );
}

Five9CrmAdapter.propTypes = {
  /** When true, the panel is visually hidden but stays mounted (preserves the Five9 session). */
  hidden: PropTypes.bool
};

Five9CrmAdapter.defaultProps = {
  hidden: false
};

export default Five9CrmAdapter;
